import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { InlineWidget } from "react-calendly";
import { Calendar as CalendarIcon, Clock, Phone, Send } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { TimezoneNotice, LocalTimePreview } from "@/components/TimezoneNotice";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const CALENDLY_URL = (process.env.REACT_APP_CALENDLY_URL || "").trim();

const FALLBACK_SERVICES = [
  "60 Minute Massage", "90 Minute Massage", "The Long Restoration — 120 Minute Massage",
  "The Signature Glow Facial", "The Grand Ritual Facial",
  "The Renewal Peel", "The Velvet Dermaplane",
  "Not sure yet — let's chat",
];

const BookingContext = createContext({ openBooking: () => {} });
export const useBooking = () => useContext(BookingContext);

export function BookingProvider({ children }) {
  const [open, setOpen] = useState(false);
  const [preset, setPreset] = useState("");
  const [serviceOptions, setServiceOptions] = useState(FALLBACK_SERVICES);

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/services`);
        if (cancel || !Array.isArray(res.data)) return;
        const names = res.data.flatMap((c) => c.items.map((i) => i.name));
        if (names.length) setServiceOptions([...names, "Not sure yet — let's chat"]);
      } catch (e) {
        // keep fallback
      }
    })();
    return () => { cancel = true; };
  }, []);

  const openBooking = useCallback((serviceName) => {
    setPreset(serviceName || "");
    setOpen(true);
  }, []);

  const value = useMemo(() => ({ openBooking }), [openBooking]);

  return (
    <BookingContext.Provider value={value}>
      {children}
      <BookingDialog
        open={open}
        onOpenChange={setOpen}
        preset={preset}
        serviceOptions={serviceOptions}
      />
    </BookingContext.Provider>
  );
}

function BookingDialog({ open, onOpenChange, preset, serviceOptions }) {
  const blank = useMemo(
    () => ({
      name: "",
      email: "",
      phone: "",
      service: preset || serviceOptions[0] || "",
      preferred_date: "",
      preferred_time: "",
      notes: "",
    }),
    [preset, serviceOptions]
  );
  const [form, setForm] = useState(blank);
  const [loading, setLoading] = useState(false);
  const [giftCode, setGiftCode] = useState("");
  const [giftInfo, setGiftInfo] = useState(null); // {valid, remaining_balance, ...}
  const [giftChecking, setGiftChecking] = useState(false);

  useEffect(() => {
    if (open) { setForm(blank); setGiftCode(""); setGiftInfo(null); }
  }, [open, blank]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const checkGiftCode = async () => {
    const code = giftCode.trim();
    if (!code) return;
    setGiftChecking(true);
    setGiftInfo(null);
    try {
      const res = await axios.get(`${API}/gift-cards/validate/${encodeURIComponent(code)}`);
      setGiftInfo(res.data);
    } catch {
      setGiftInfo({ valid: false, reason: "Could not check" });
    } finally { setGiftChecking(false); }
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      // Always submit time as Pacific so Bella's inbox is unambiguous.
      const noteWithGift = giftInfo?.valid
        ? `${form.notes ? form.notes + "\n\n" : ""}Gift certificate: ${giftCode.trim().toUpperCase()} (balance $${giftInfo.remaining_balance})`.trim()
        : form.notes;
      const payload = {
        ...form,
        notes: noteWithGift,
        preferred_time: form.preferred_time ? `${form.preferred_time} PT` : "",
      };
      const res = await axios.post(`${API}/booking`, payload);
      const forwarded = res?.data?.email_forwarded;
      toast.success("Request received.", {
        description: forwarded
          ? "Bella has been notified by email and will confirm your appointment shortly."
          : "We saved your request — Bella will follow up shortly. (Email notification pending.)",
      });
      onOpenChange(false);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg =
        typeof detail === "string"
          ? detail
          : Array.isArray(detail) && detail.length
          ? detail.map((d) => d.msg).join("; ")
          : "Please try again or call us directly at 916-243-5514.";
      toast.error("Could not send request", { description: msg });
    } finally {
      setLoading(false);
    }
  };

  const today = new Date().toISOString().slice(0, 10);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="booking-dialog"
        className={`bg-[#FAF6E8] border-[#D9CFA0] text-[#0F1F4E] ${
          CALENDLY_URL ? "sm:max-w-3xl" : "sm:max-w-xl"
        } w-[calc(100vw-1.5rem)] max-h-[92svh] p-0 overflow-hidden flex flex-col`}
      >
        <div className="bg-[#0F1F4E] px-6 md:px-8 py-6 relative shrink-0">
          <DialogHeader>
            <span className="overline-gold mb-2 inline-block">Reserve a moment</span>
            <DialogTitle className="font-serif text-3xl md:text-4xl font-light text-[#F8F1D8] tracking-tight">
              Book with <span className="italic text-[#EAD584]">Bella</span>
            </DialogTitle>
            <DialogDescription className="text-[#D6D6BD] font-light pt-1">
              In-studio sessions only. {CALENDLY_URL ? "Pick an open time on the live calendar below." : "Bella will confirm by phone or email."}
            </DialogDescription>
          </DialogHeader>
        </div>

        <div className="overflow-y-auto flex-1 overscroll-contain">
          <div className="px-6 md:px-8 pt-5 space-y-3">
            <div
              data-testid="booking-facials-notice"
              className="text-xs leading-relaxed bg-gradient-to-r from-[#EAD584]/40 to-[#EAD584]/20 border border-[#EAD584] rounded-md px-4 py-3 text-[#0F1F4E] font-light"
            >
              <span className="font-medium">Facials have arrived.</span> Signature Glow,
              Grand Ritual, Renewal Peel, and Velvet Dermaplane are now bookable — pick your
              treatment below.
            </div>
            <div
              data-testid="booking-in-home-notice"
              className="text-xs leading-relaxed bg-[#F2EBD3] border border-[#D9CFA0] rounded-md px-4 py-3 text-[#0F1F4E]/80 font-light"
            >
              Looking for an <span className="font-medium">in-home / mobile session?</span>{" "}
              <button
                type="button"
                data-testid="booking-switch-in-home"
                onClick={() => {
                  onOpenChange(false);
                  setTimeout(() => {
                    const el = document.getElementById("contact");
                    if (el) el.scrollIntoView({ behavior: "smooth" });
                    window.dispatchEvent(
                      new CustomEvent("prefill-contact", {
                        detail: {
                          message:
                            "Hi Bella — I'd like to request an in-home session.\n\nPreferred date/time: __\nAddress (city/zip): __\n\nThank you!",
                        },
                      })
                    );
                  }, 250);
                }}
                className="underline font-medium text-[#0F1F4E] hover:text-[#C9A65A]"
              >
                Request via contact form
              </button>{" "}
              and Bella will follow up directly.
            </div>
            <div
              data-testid="booking-gift-code-strip"
              className="text-xs leading-relaxed bg-[#FAF6E8] border border-[#D9CFA0] rounded-md px-4 py-3 text-[#0F1F4E]/85 font-light flex flex-wrap items-center gap-2"
            >
              <span className="font-medium">Have a gift certificate?</span>
              <input
                type="text"
                value={giftCode}
                onChange={(e) => { setGiftCode(e.target.value); setGiftInfo(null); }}
                data-testid="booking-gift-code-input"
                placeholder="BB-XXXX-XXXX"
                className="flex-1 min-w-[8rem] bg-white border border-[#D9CFA0] rounded px-2 py-1 text-xs font-mono outline-none focus:border-[#0F1F4E]"
              />
              <button
                type="button"
                onClick={checkGiftCode}
                disabled={giftChecking || !giftCode.trim()}
                data-testid="booking-gift-code-check"
                className="px-3 py-1 rounded-full text-[10px] uppercase tracking-wider bg-[#0F1F4E] text-[#EAD584] hover:bg-[#1B3370] disabled:opacity-50"
              >
                {giftChecking ? "Checking…" : "Apply"}
              </button>
              {giftInfo && (
                <div
                  data-testid="booking-gift-code-result"
                  className={`w-full text-[11px] mt-1 ${giftInfo.valid ? "text-[#0F1F4E]" : "text-red-700"}`}
                >
                  {giftInfo.valid
                    ? `✓ Balance $${giftInfo.remaining_balance} — Bella will apply it at your appointment.`
                    : `Code not valid${giftInfo.reason ? ` (${giftInfo.reason})` : ""}.`}
                </div>
              )}
            </div>
          </div>

          {CALENDLY_URL ? (
            <div className="px-2 md:px-4 pb-6" data-testid="booking-calendly-widget">
              <div className="px-4 pb-3">
                <TimezoneNotice />
              </div>
              <InlineWidget
                url={CALENDLY_URL}
                styles={{ height: "640px", minWidth: "280px" }}
                prefill={preset ? { customAnswers: { a1: preset } } : undefined}
                pageSettings={{
                  backgroundColor: "FAF6E8",
                  primaryColor: "0F1F4E",
                  textColor: "0F1F4E",
                  hideEventTypeDetails: false,
                  hideLandingPageDetails: false,
                }}
              />
              <div className="px-4 pt-3 pb-1 flex items-center justify-between flex-wrap gap-3">
                <a
                  href="tel:9162435514"
                  data-testid="booking-call-instead"
                  className="inline-flex items-center gap-2 text-sm text-[#0F1F4E] link-underline text-link-hover"
                >
                  <Phone className="w-4 h-4" /> Or call 916-243-5514
                </a>
                <span className="text-xs text-[#0F1F4E]/60 font-light">
                  Powered by Calendly
                </span>
              </div>
            </div>
          ) : (
            <form onSubmit={onSubmit} data-testid="booking-form" className="px-6 md:px-8 pb-6 space-y-5">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <label className="block">
                  <span className="overline block mb-2">Name</span>
                  <input
                    required
                    value={form.name}
                    onChange={set("name")}
                    data-testid="booking-name-input"
                    className="w-full bg-transparent border-b border-[#0F1F4E]/30 focus:border-[#0F1F4E] outline-none py-2 text-base font-light"
                    placeholder="Your name"
                  />
                </label>
                <label className="block">
                  <span className="overline block mb-2">Email</span>
                  <input
                    required
                    type="email"
                    value={form.email}
                    onChange={set("email")}
                    data-testid="booking-email-input"
                    className="w-full bg-transparent border-b border-[#0F1F4E]/30 focus:border-[#0F1F4E] outline-none py-2 text-base font-light"
                    placeholder="you@email.com"
                  />
                </label>
                <label className="block md:col-span-2">
                  <span className="overline block mb-2">Phone</span>
                  <input
                    required
                    value={form.phone}
                    onChange={set("phone")}
                    data-testid="booking-phone-input"
                    className="w-full bg-transparent border-b border-[#0F1F4E]/30 focus:border-[#0F1F4E] outline-none py-2 text-base font-light"
                    placeholder="(916) 000-0000"
                  />
                </label>
                <label className="block md:col-span-2">
                  <span className="overline block mb-2">Service</span>
                  <select
                    required
                    value={form.service}
                    onChange={set("service")}
                    data-testid="booking-service-select"
                    className="w-full bg-transparent border-b border-[#0F1F4E]/30 focus:border-[#0F1F4E] outline-none py-2 text-base font-light appearance-none"
                  >
                    {!serviceOptions.includes(form.service) && form.service && (
                      <option value={form.service}>{form.service}</option>
                    )}
                    {serviceOptions.map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="block">
                  <span className="overline mb-2 flex items-center gap-2">
                    <CalendarIcon className="w-3.5 h-3.5" /> Preferred date
                  </span>
                  <input
                    required
                    type="date"
                    min={today}
                    value={form.preferred_date}
                    onChange={set("preferred_date")}
                    data-testid="booking-date-input"
                    className="w-full bg-transparent border-b border-[#0F1F4E]/30 focus:border-[#0F1F4E] outline-none py-2 text-base font-light"
                  />
                </label>
                <label className="block">
                  <span className="overline mb-2 flex items-center gap-2">
                    <Clock className="w-3.5 h-3.5" /> Preferred time
                  </span>
                  <input
                    required
                    type="time"
                    value={form.preferred_time}
                    onChange={set("preferred_time")}
                    data-testid="booking-time-input"
                    className="w-full bg-transparent border-b border-[#0F1F4E]/30 focus:border-[#0F1F4E] outline-none py-2 text-base font-light"
                  />
                  <span className="text-[10px] uppercase tracking-wider text-[#0F1F4E]/60 mt-1 inline-block">
                    Pacific Time (Sacramento)
                  </span>
                </label>
                <div className="md:col-span-2 -mt-2">
                  <TimezoneNotice compact />
                  <LocalTimePreview date={form.preferred_date} time={form.preferred_time} />
                </div>
                <label className="block md:col-span-2">
                  <span className="overline block mb-2">Notes (optional)</span>
                  <textarea
                    rows={3}
                    value={form.notes}
                    onChange={set("notes")}
                    data-testid="booking-notes-input"
                    className="w-full bg-transparent border-b border-[#0F1F4E]/30 focus:border-[#0F1F4E] outline-none py-2 text-base font-light resize-none"
                    placeholder="Anything Bella should know — sensitivities, occasion, second-choice times…"
                  />
                </label>
              </div>

              <div className="flex items-center justify-between flex-wrap gap-4 pt-2">
                <a
                  href="tel:9162435514"
                  data-testid="booking-call-instead"
                  className="inline-flex items-center gap-2 text-sm text-[#0F1F4E] link-underline text-link-hover"
                >
                  <Phone className="w-4 h-4" /> Or call 916-243-5514
                </a>
                <button
                  type="submit"
                  disabled={loading}
                  data-testid="booking-submit"
                  className="btn-shine inline-flex items-center gap-2 px-7 py-3.5 rounded-full text-sm tracking-wider uppercase text-[#0F1F4E] bg-[#EAD584] hover:bg-[#F0DC8A] transition-colors disabled:opacity-60"
                >
                  {loading ? "Sending…" : "Send request"}
                  {!loading && <Send className="w-4 h-4" />}
                </button>
              </div>
            </form>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
